import { COMPANY_ID_HEADER } from '@helix/auth';
import { getAuthHeaders, USER_ID_HEADER } from './graphql-client';
import { resolveConfiguredOrLocalApiBaseUrl } from './local-api-origin';
import { resolveBrowserAuthHeaders } from './authApi';

type ApiFetchOptions = Omit<RequestInit, 'body'> & {
  body?: BodyInit | null;
  json?: unknown;
};

function resolveApiBaseUrl(): string {
  return resolveConfiguredOrLocalApiBaseUrl(process.env.NEXT_PUBLIC_API_URL, {
    nodeEnv: process.env.NODE_ENV,
  });
}

function resolveUrl(pathOrUrl: string): string {
  if (
    typeof window !== 'undefined' &&
    pathOrUrl.startsWith('/scenarios/workforce-reduction')
  ) {
    return `/api/workforce-reduction${pathOrUrl.slice('/scenarios/workforce-reduction'.length)}`;
  }

  const apiBaseUrl = resolveApiBaseUrl();
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) {
    return pathOrUrl;
  }
  if (!apiBaseUrl) {
    if (pathOrUrl.startsWith('/')) return pathOrUrl;
    return `/${pathOrUrl}`;
  }
  if (pathOrUrl.startsWith('/')) {
    return `${apiBaseUrl}${pathOrUrl}`;
  }
  return `${apiBaseUrl}/${pathOrUrl}`;
}

export async function apiFetch(pathOrUrl: string, opts: ApiFetchOptions = {}): Promise<Response> {
  const { json, body, headers, ...rest } = opts;
  if (json !== undefined && body !== undefined) {
    throw new Error('apiFetch: provide either `json` or `body`, not both');
  }

  const resolvedHeaders = new Headers(headers);
  const e2eHeaders = getAuthHeaders();
  for (const [key, value] of Object.entries(e2eHeaders)) {
    if (!resolvedHeaders.has(key)) {
      resolvedHeaders.set(key, value);
    }
  }

  if (!resolvedHeaders.has(COMPANY_ID_HEADER) || !resolvedHeaders.has(USER_ID_HEADER)) {
    const browserAuthHeaders = await resolveBrowserAuthHeaders();
    for (const [key, value] of Object.entries(browserAuthHeaders)) {
      if (!resolvedHeaders.has(key)) {
        resolvedHeaders.set(key, value);
      }
    }
  }
  let resolvedBody = body;

  if (json !== undefined) {
    resolvedBody = JSON.stringify(json);
    if (!resolvedHeaders.has('content-type')) {
      resolvedHeaders.set('content-type', 'application/json');
    }
  }

  return fetch(resolveUrl(pathOrUrl), {
    ...rest,
    credentials: 'include',
    headers: resolvedHeaders,
    body: resolvedBody,
  });
}
